import { useState, useRef, useEffect } from "react";
import { ChevronDown, Radio, Brain, Aperture, Wrench, FlaskConical, Layers, BarChart4, Target, FileText, Settings, Zap } from "lucide-react";

interface ModuleInfo {
  id: string;
  label: string;
  icon: any;
  category: string;
}

const MODULE_MAP: Record<string, ModuleInfo> = {
  dashboard: { id: "dashboard", label: "Brand Monitoring", icon: Radio, category: "Measure" },
  cognition: { id: "cognition", label: "AI Reasoning Path", icon: Brain, category: "Measure" },
  "deep-signal": { id: "deep-signal", label: "Deep Signal", icon: Aperture, category: "Measure" },
  "fix-actions": { id: "fix-actions", label: "Action Items", icon: Wrench, category: "Optimize" },
  "ai-sandbox": { id: "ai-sandbox", label: "Test & Refine", icon: FlaskConical, category: "Optimize" },
  "brand-tools": { id: "brand-tools", label: "Brand Controls", icon: Layers, category: "Optimize" },
  "analytics-overview": { id: "analytics-overview", label: "Performance", icon: BarChart4, category: "Track" },
  "prompt-tracking": { id: "prompt-tracking", label: "Prompt Intelligence", icon: Target, category: "Track" },
  "report-generator": { id: "report-generator", label: "Insights Studio", icon: FileText, category: "Track" },
  settings: { id: "settings", label: "Settings", icon: Settings, category: "Manage" },
  integrations: { id: "integrations", label: "Integrations", icon: Zap, category: "Manage" },
};

interface ModuleBreadcrumbProps {
  currentModuleId: string;
  onNavigate?: (moduleId: string) => void;
}

export function ModuleBreadcrumb({ currentModuleId, onNavigate }: ModuleBreadcrumbProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentModule = MODULE_MAP[currentModuleId];
  if (!currentModule) return null;

  const { category, label } = currentModule;

  // Get all modules in the same category
  const categoryModules = Object.values(MODULE_MAP).filter(
    (module) => module.category === category
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleCategoryClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleModuleClick = (moduleId: string) => {
    setIsDropdownOpen(false);
    if (onNavigate) {
      onNavigate(moduleId);
    }
  };

  return (
    <div 
      className="flex items-center mb-6"
      style={{
        gap: "8px",
      }}
    >
      {/* Category Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={handleCategoryClick}
          className="flex items-center transition-colors rounded"
          style={{
            gap: "6px",
            padding: "4px 8px",
            marginLeft: "-8px",
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            fontWeight: "var(--font-weight-medium)",
            backgroundColor: isDropdownOpen ? "var(--muted)" : "transparent",
            borderRadius: "var(--radius)",
          }}
          onMouseEnter={(e) => {
            if (!isDropdownOpen) {
              e.currentTarget.style.backgroundColor = "var(--muted)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isDropdownOpen) {
              e.currentTarget.style.backgroundColor = "transparent";
            }
          }}
        >
          {category}
          <ChevronDown
            className="w-3.5 h-3.5 transition-transform"
            style={{
              transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div
            className="absolute top-full left-0 shadow-lg z-50"
            style={{
              marginTop: "4px",
              minWidth: "200px",
              backgroundColor: "var(--background)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--elevation-lg)",
            }}
          >
            <div style={{ padding: "4px 0" }}>
              {categoryModules.map((module) => {
                const Icon = module.icon;
                const isActive = module.id === currentModuleId;

                return (
                  <button
                    key={module.id}
                    onClick={() => handleModuleClick(module.id)}
                    className="w-full flex items-center transition-colors text-left"
                    style={{
                      gap: "12px",
                      padding: "8px 12px",
                      fontSize: "var(--text-sm)",
                      color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                      fontWeight: isActive ? "var(--font-weight-semibold)" : "var(--font-weight-normal)",
                      backgroundColor: isActive ? "var(--muted)" : "transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "var(--muted-30)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{module.label}</span>
                    {isActive && (
                      <div
                        className="ml-auto w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: "var(--alpha-teal)" }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Separator */}
      <span
        style={{
          fontSize: "var(--text-sm)",
          color: "var(--muted-foreground)",
          fontWeight: "var(--font-weight-medium)",
        }}
      >
        /
      </span>

      {/* Current Module */}
      <span
        style={{
          fontSize: "var(--text-sm)",
          color: "var(--muted-foreground)",
          fontWeight: "var(--font-weight-medium)",
        }}
      >
        {label}
      </span>
    </div>
  );
}